import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { TextField } from '@shared/ui/primitives/TextField';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { toast } from '@shared/ui/feedback/Toast';
import { toApiError } from '@shared/api/errors';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { Stepper } from '@shared/ui/lists/Stepper';
import {
  ActionButton,
  CheckboxOption,
  FormActions,
  FormSection,
  FormStack,
  RadioOption,
  SelectableTile,
  TextInputField,
} from '@shared/ui/forms/FormFields';
import { pageCopy } from '@shared/lib/pageCopy';
import {
  fetchPanicContacts,
  searchPanicContacts,
  upsertPanicContact,
  type PanicContact,
  type PanicContactSearchResult,
  type PanicContactSource,
} from './api';

type ContactMode = PanicContactSource;
type ContactStep = 'contact' | 'notifications' | 'review';

const STEP_LABELS: string[] = ['Contact', 'Alerts', 'Review'];

function panicErrorMessage(error: unknown): string {
  const apiError = toApiError(error);
  if (!/^Request failed with status code \d{3}$/.test(apiError.message.trim())) {
    return apiError.message;
  }

  const message = findNestedErrorMessage(error);
  return message ?? apiError.message;
}

function findNestedErrorMessage(value: unknown, depth = 0): string | undefined {
  if (depth > 4 || value == null) return undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value !== 'object') return undefined;

  const record = value as Record<string, unknown>;
  const directError = typeof record.error === 'string' ? record.error.trim() : '';
  if (directError) return directError;

  const directMessage = typeof record.message === 'string' ? record.message.trim() : '';
  if (directMessage && !/^Request failed with status code \d{3}$/.test(directMessage)) {
    return directMessage;
  }

  return (
    findNestedErrorMessage(record.raw, depth + 1) ||
    findNestedErrorMessage(record.response, depth + 1) ||
    findNestedErrorMessage(record.data, depth + 1) ||
    findNestedErrorMessage(record.cause, depth + 1)
  );
}

function decodeReturnTo(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch {
    return fallback;
  }
}

function searchResultFromContact(contact: PanicContact): PanicContactSearchResult {
  return {
    core_user_id: contact.core_user_id ?? '',
    display_name: contact.name,
    first_name: null,
    last_name: null,
    email: contact.email ?? '',
    profile_image_url: null,
    is_same_community: Boolean(contact.same_community_user_id),
    same_community_member_id: contact.same_community_member_id ?? null,
    same_community_user_id: contact.same_community_user_id ?? null,
    phone: contact.mobile || null,
    push_allowed: Boolean(contact.same_community_user_id),
  };
}

/**
 * Add / edit emergency contact — mirrors the legacy member-portal flow:
 * choose a community member from search, or add an outside/manual contact.
 */
export function PanicContactForm(): JSX.Element {
  const { contactId } = useParams<{ contactId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(contactId);
  const copy = pageCopy().panic.contactForm;
  const formCopy = isEdit ? copy.edit : copy.new;

  const returnTo = decodeReturnTo(searchParams.get('returnTo'), '/app/panic?tab=contacts');

  const contactsQuery = useQuery({
    queryKey: ['panic-contacts'],
    queryFn: fetchPanicContacts,
    enabled: isEdit,
  });

  const existing = useMemo(
    () => contactsQuery.data?.find((contact) => contact.contact_id === contactId),
    [contactsQuery.data, contactId],
  );

  const [mode, setMode] = useState<ContactMode>('manual');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PanicContactSearchResult[]>([]);
  const [selectedSearchResult, setSelectedSearchResult] = useState<PanicContactSearchResult | null>(null);
  const [notifySms, setNotifySms] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPush, setNotifyPush] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [step, setStep] = useState<ContactStep>('contact');
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;

    setMode(existing.contact_source);
    setName(existing.name);
    setMobile(existing.mobile);
    setEmail(existing.email ?? '');
    setNotifySms(existing.notify_via_sms);
    setNotifyEmail(existing.notify_via_email);
    setNotifyPush(existing.notify_via_push);
    setIsActive(existing.is_active);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setStep('contact');
    setSelectedSearchResult(
      existing.contact_source === 'core_user' ? searchResultFromContact(existing) : null,
    );
  }, [existing]);

  const currentPhone = mode === 'core_user'
    ? selectedSearchResult?.phone?.trim() ?? ''
    : mobile.trim();
  const currentEmail = mode === 'core_user'
    ? selectedSearchResult?.email?.trim() ?? ''
    : email.trim();
  const pushAvailable = mode === 'core_user' && Boolean(selectedSearchResult?.push_allowed);

  const canSaveBase = mode === 'core_user'
    ? Boolean(selectedSearchResult?.core_user_id)
    : name.trim().length > 0;
  const hasSelectedChannel = notifySms || notifyEmail || (pushAvailable && notifyPush);
  const canSave =
    canSaveBase &&
    hasSelectedChannel &&
    (!notifySms || currentPhone.length > 0) &&
    (!notifyEmail || currentEmail.length > 0) &&
    (!notifyPush || pushAvailable);

  const searchMutation = useMutation({
    mutationFn: (query: string) => searchPanicContacts(query),
    onSuccess: (results) => {
      setSearchResults(results);
      setSearchError(results.length === 0 ? 'No users matched your search.' : null);
    },
    onError: (error: unknown) => {
      setSearchResults([]);
      setSearchError(toApiError(error).message || 'Search failed.');
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmedName = mode === 'core_user'
        ? selectedSearchResult?.display_name.trim() ?? ''
        : name.trim();
      const trimmedMobile = currentPhone;
      const trimmedEmail = currentEmail;

      if (mode === 'core_user' && !selectedSearchResult?.core_user_id) {
        throw new Error('Select a community member before saving.');
      }
      if (mode === 'manual' && !trimmedName) {
        throw new Error('Name is required');
      }
      if (!notifySms && !notifyEmail && !notifyPush) {
        throw new Error('Choose at least one way to notify this contact');
      }
      if (notifySms && !trimmedMobile) {
        throw new Error('A mobile number is required for SMS alerts');
      }
      if (notifyEmail && !trimmedEmail) {
        throw new Error('An email address is required for email alerts');
      }
      if (notifyPush && !pushAvailable) {
        throw new Error('Push notifications are only available for community members');
      }

      return upsertPanicContact({
        contact_id: contactId,
        contact_source: mode,
        core_user_id: mode === 'core_user' ? selectedSearchResult?.core_user_id ?? null : null,
        name: trimmedName,
        mobile: trimmedMobile,
        email: trimmedEmail || undefined,
        notify_via_sms: notifySms,
        notify_via_email: notifyEmail,
        notify_via_push: pushAvailable ? notifyPush : false,
        is_active: isActive,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['panic-contacts'] });
      toast.success(isEdit ? 'Contact updated' : 'Contact added');
      navigate(returnTo, { replace: true });
    },
    onError: (error: unknown) => {
      const message = panicErrorMessage(error);
      toast.danger('Could not save', message);
    },
  });

  const stepIndex = STEP_LABELS.findIndex((_, index) => {
    if (step === 'contact') return index === 0;
    if (step === 'notifications') return index === 1;
    return index === 2;
  });

  const reviewName = mode === 'core_user'
    ? selectedSearchResult?.display_name.trim() ?? ''
    : name.trim();
  const reviewChannels = [
    notifySms ? `SMS${currentPhone ? ` · ${currentPhone}` : ''}` : null,
    notifyEmail ? `Email${currentEmail ? ` · ${currentEmail}` : ''}` : null,
    notifyPush && pushAvailable ? 'Push notification' : null,
  ].filter(Boolean) as string[];
  const contactStepHeading = isEdit ? 'Edit contact details' : 'Choose the contact';
  const contactStepDescription = isEdit
    ? 'Review who this contact is and update how they should be reached during an SOS.'
    : 'Start by choosing who should be alerted in an emergency.';

  const applyMode = (nextMode: ContactMode) => {
    if (isEdit) return;
    setMode(nextMode);
    setStep('contact');
    setSelectedSearchResult(null);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setNotifySms(false);
    setNotifyEmail(false);
    setNotifyPush(false);
  };

  const validateContactStep = (): boolean => {
    if (mode === 'core_user' && !selectedSearchResult?.core_user_id) {
      toast.danger('Check the form', 'Select a community member before continuing.');
      return false;
    }

    if (mode === 'manual' && !name.trim()) {
      toast.danger('Check the form', 'Enter the contact name before continuing.');
      return false;
    }
    return true;
  };

  const validateNotificationsStep = (): boolean => {
    if (!notifySms && !notifyEmail && !notifyPush) {
      toast.danger('Check the form', 'Choose at least one way to notify this contact.');
      return false;
    }
    if (notifySms && !currentPhone) {
      toast.danger('Check the form', 'A mobile number is required for SMS alerts.');
      return false;
    }
    if (notifyEmail && !currentEmail) {
      toast.danger('Check the form', 'An email address is required for email alerts.');
      return false;
    }
    if (notifyPush && !pushAvailable) {
      toast.danger('Check the form', 'Push notifications are only available for community members.');
      return false;
    }
    return true;
  };

  const canAdvanceContact =
    (mode === 'core_user' && !!selectedSearchResult?.core_user_id) ||
    (mode === 'manual' && !!name.trim());
  const canAdvanceNotifications =
    (notifySms || notifyEmail || notifyPush) &&
    (!notifySms || !!currentPhone) &&
    (!notifyEmail || !!currentEmail) &&
    (!notifyPush || !!pushAvailable);

  const maxReachableStepIndex =
    step === 'contact'
      ? (canAdvanceContact ? (canAdvanceNotifications ? 2 : 1) : 0)
      : step === 'notifications'
        ? (canAdvanceNotifications ? 2 : 1)
        : 2;

  const handleStepSelect = (index: number) => {
    if (index === stepIndex) return;
    if (index < 0 || index > maxReachableStepIndex) return;

    if (index === 0) {
      setStep('contact');
      return;
    }
    if (index === 1) {
      setStep('notifications');
      return;
    }
    setStep('review');
  };

  const goNext = () => {
    if (step === 'contact') {
      if (validateContactStep()) {
        setStep('notifications');
      }
      return;
    }

    if (step === 'notifications') {
      if (validateNotificationsStep()) {
        setStep('review');
      }
    }
  };

  const goBack = () => {
    if (step === 'review') {
      setStep('notifications');
      return;
    }
    if (step === 'notifications') {
      setStep('contact');
    }
  };

  const runSearch = () => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchResults([]);
      setSearchError('Enter a name or email to search.');
      return;
    }

    setSearchError(null);
    searchMutation.mutate(query);
  };

  const selectSearchResult = (result: PanicContactSearchResult) => {
    if (selectedSearchResult?.core_user_id === result.core_user_id) {
      clearSelectedSearchResult();
      return;
    }

    setSelectedSearchResult(result);
    setSearchError(null);

    if (!result.phone && notifySms) {
      setNotifySms(false);
    }
    if (!result.email && notifyEmail) {
      setNotifyEmail(false);
    }
    if (!result.push_allowed && notifyPush) {
      setNotifyPush(false);
    }
  };

  const clearSelectedSearchResult = () => {
    setSelectedSearchResult(null);
    setNotifySms(false);
    setNotifyEmail(false);
    setNotifyPush(false);
  };

  if (isEdit && contactsQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="mt-4 h-24 rounded-2xl" />
      </div>
    );
  }

  if (isEdit && contactsQuery.isError) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <ErrorState onRetry={() => contactsQuery.refetch()} />
      </div>
    );
  }

  if (isEdit && contactsQuery.isSuccess && !existing) {
    return (
      <div className="min-h-full bg-paper px-5 pt-safe-or-6">
        <ErrorState
          title="Contact not found"
          description="It may have been removed."
          onRetry={() => navigate(returnTo)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        onBack={() => navigate(returnTo)}
        eyebrow={formCopy.eyebrow}
        title={formCopy.title}
        subtitle={formCopy.subtitle}
        titleClassName="whitespace-pre-line leading-tight"
      />

      <FormStack className="pt-5">
        <div className="flex flex-col gap-5">
          <FormSection className="pb-5" contentClassName="gap-3">
            <div>
              <h2 className="text-label font-semibold text-ink">
                {step === 'contact' ? contactStepHeading : step === 'notifications' ? 'Set up alerts' : 'Review details'}
              </h2>
              <p className="mt-1 text-caption text-ink-muted">
                {step === 'contact'
                  ? contactStepDescription
                  : step === 'notifications'
                    ? 'Pick how this person should be notified when an SOS is raised.'
                    : 'Check the details before you save this panic contact.'}
              </p>
            </div>
            <Stepper
              total={STEP_LABELS.length}
              current={stepIndex}
              labels={STEP_LABELS}
              onStepSelect={handleStepSelect}
              isStepAccessible={(index) => index <= maxReachableStepIndex}
            />
          </FormSection>

          {step === 'contact' ? (
            <>
              {!isEdit ? (
                <FormSection
                  title="Contact type"
                  description="Choose a community member for platform-linked alerts, or add someone outside the estate manually."
                >
                  <RadioOption<ContactMode>
                    name="contact-mode"
                    value="manual"
                    selected={mode === 'manual'}
                    onChange={applyMode}
                    label="Outside contact"
                    description="Add someone by name, mobile number, and email address."
                  />
                  <RadioOption<ContactMode>
                    name="contact-mode"
                    value="core_user"
                    selected={mode === 'core_user'}
                    onChange={applyMode}
                    label="Community member"
                    description="Search for a platform user and use their available channels."
                  />
                </FormSection>
              ) : null}

            {mode === 'manual' ? (
              <FormSection title="Contact details">
                <TextInputField
                  label="Name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Jane Smith"
                  autoComplete="name"
                />

                <TextInputField
                  label="Mobile"
                  value={mobile}
                  onChange={(event) => {
                    const next = event.target.value;
                    setMobile(next);
                    if (!next.trim() && notifySms) {
                      setNotifySms(false);
                    }
                  }}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g. 082 123 4567"
                />

                <TextInputField
                  label="Email"
                  value={email}
                  onChange={(event) => {
                    const next = event.target.value;
                    setEmail(next);
                    if (!next.trim() && notifyEmail) {
                      setNotifyEmail(false);
                    }
                  }}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Optional"
                />
              </FormSection>
            ) : isEdit ? (
              <FormSection
                title="Linked community member"
                description="This contact is linked to a resident profile. Their identity details come from the platform, while you can still update alert preferences below."
              >
                <div className="rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-3 text-caption text-primary-700">
                  To change this to a different resident, remove the contact and add a new community member contact.
                </div>

                <TextInputField
                  label="Resident"
                  value={selectedSearchResult?.display_name ?? name}
                  disabled
                  readOnly
                />

                <TextInputField
                  label="Mobile"
                  value={currentPhone}
                  disabled
                  readOnly
                  placeholder="No mobile number available"
                  optional
                />

                <TextInputField
                  label="Email"
                  value={currentEmail}
                  disabled
                  readOnly
                  placeholder="No email address available"
                  optional
                />
              </FormSection>
            ) : (
              <FormSection
                title="Community member"
                description="Search by name or email, then select the resident you want to notify."
              >
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                  <TextField
                    label="Search by name or email"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        runSearch();
                      }
                    }}
                    leadingIcon={<Search className="h-4 w-4" strokeWidth={1.5} />}
                  />
                  <Button
                    variant="secondary"
                    className="w-full sm:w-auto"
                    loading={searchMutation.isPending}
                    onClick={runSearch}
                  >
                    Search
                  </Button>
                </div>

                {searchError ? (
                  <div className="rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-3 text-caption text-primary-700">
                    {searchError}
                  </div>
                ) : null}

                {searchResults.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {searchResults.map((result) => {
                      const selected = selectedSearchResult?.core_user_id === result.core_user_id;
                      return (
                        <SelectableTile
                          key={result.core_user_id}
                          title={result.display_name}
                          description={result.email}
                          detail={result.phone ?? undefined}
                          selected={selected}
                          selectionType="checkbox"
                          onSelect={() => selectSearchResult(result)}
                          badges={
                            result.push_allowed ? (
                              <span className="rounded-pill bg-primary-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-primary-700">
                                Push
                              </span>
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </div>
                ) : null}
              </FormSection>
            )}
            </>
          ) : null}

          {step === 'notifications' ? (
            <>
              <FormSection title="Contact status">
                <CheckboxOption
                  label="Active contact"
                  description="Pause this contact without removing them from your list."
                  checked={isActive}
                  onChange={setIsActive}
                />
              </FormSection>

              <FormSection
                title="Notifications"
                description="Choose how this contact should be reached when an SOS is raised."
              >
                <CheckboxOption
                  label="SMS"
                  description={
                    currentPhone
                      ? `Send a text message to ${currentPhone}`
                      : 'Add a mobile number or select a member with a phone number.'
                  }
                  checked={notifySms}
                  disabled={!currentPhone}
                  onChange={setNotifySms}
                />
                <CheckboxOption
                  label="Email"
                  description={
                    currentEmail
                      ? `Send an email to ${currentEmail}`
                      : 'Add an email address or select a member with an email address.'
                  }
                  checked={notifyEmail}
                  disabled={!currentEmail}
                  onChange={setNotifyEmail}
                />
                <CheckboxOption
                  label="Push"
                  description={
                    pushAvailable
                      ? 'Send an in-app notification to this community member.'
                      : 'Push is only available for platform users in this community.'
                  }
                  checked={notifyPush}
                  disabled={!pushAvailable}
                  onChange={setNotifyPush}
                />
              </FormSection>
            </>
          ) : null}

          {step === 'review' ? (
            <>
              <FormSection title="Contact summary">
                <SelectableTile
                  title={reviewName || 'Unnamed contact'}
                  description={mode === 'core_user' ? 'Community member' : 'Outside contact'}
                  detail={currentEmail || currentPhone || 'No channel selected yet'}
                  selected
                  selectionType="checkbox"
                />
              </FormSection>

              <FormSection title="Alert methods">
                <div className="flex flex-col gap-2">
                  {reviewChannels.map((channel) => (
                    <div
                      key={channel}
                      className="rounded-lg bg-sunken px-3.5 py-3 text-body-sm text-ink-secondary"
                    >
                      {channel}
                    </div>
                  ))}
                </div>
              </FormSection>

              <FormSection title="Contact status">
                <div className="rounded-lg bg-sunken px-3.5 py-3 text-body-sm text-ink-secondary">
                  {isActive ? 'Active contact' : 'Paused contact'}
                </div>
              </FormSection>
            </>
          ) : null}

          <FormActions sticky>
            {step === 'contact' ? (
              <ActionButton onClick={goNext}>
                Next
              </ActionButton>
            ) : step === 'notifications' ? (
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" width="full" onClick={goBack}>
                  Back
                </Button>
                <Button type="button" width="full" onClick={goNext}>
                  Next
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button type="button" variant="secondary" width="full" onClick={goBack}>
                  Back
                </Button>
                <ActionButton
                  disabled={!canSave}
                  loading={saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {isEdit ? 'Save changes' : 'Add contact'}
                </ActionButton>
              </div>
            )}
          </FormActions>
        </div>
      </FormStack>
    </div>
  );
}
